import styled from '@emotion/styled'

/**
 * 페이지 wrapper.
 * - WebView 안에서 풀 높이 — 100dvh로 모바일 주소창 변동 대응
 * - 좌우 가로 스크롤 차단 — `overflow-x: clip` 사용
 *   `hidden` 은 자체 scroll container 를 만들어 ① 자식의 position:sticky 가 깨지고
 *   ② window.scrollTo 가 PageWrapper 외부에 적용되지 않음. `clip` 은 painting clip
 *   만 적용해 sticky/스크롤 동작을 유지.
 * - 하단 safe-area는 추후 CTA 영역에서 처리
 */
export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100dvh;
  background: #ffffff;
  overflow-x: clip;
`

/**
 * 지도 + 시군구 리스트 영역.
 * - bg subtle gray (#f5f5f6) — Hero/Tabs의 white와 구분
 * - flex: 1로 남은 viewport 채워서 페이지 끝까지 회색 유지
 * - 시도 탭 ↔ 지도 사이 60px (피그마 17769:47949 padding-top), 좌우 20px, 하단 24px
 * - gap 24 — 내부 섹션(지도, 리스트) 간격
 */
export const ContentSection = styled.section`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 24px;
  width: 100%;
  padding: 24px 20px;
  background: #f5f5f6;
`

/**
 * 시군구 그룹 리스트 — 그룹(SiGunGuGroup) 간 gap + 하단 여백.
 *
 * gap:
 *  - compact=false (기본, 경기 등): 20px — SiGunGuGroup 헤더 + 카드 N개 묶음 간 시각적 분리
 *  - compact=true (인천): 8px — SiGunGuGroup 헤더 숨겨 카드 1개씩 평면 나열되므로
 *    아코디언 간격이 곧 그룹 간격. 디자이너 요청대로 카드 간 8px 통일
 *
 * padding-bottom 36px: ContentSection padding-bottom 24 + 36 = 페이지 하단 60px 여백.
 * DistrictList 는 isFullCoverage=false 일 때만 노출되므로 자동으로 경기/인천 한정 적용
 * (서울은 isFullCoverage=true 라 DistrictList 자체 미노출).
 *
 * 시도 헤더가 있을 때(siDoList.length > 1) 시도 헤더도 같은 gap 으로 흐름.
 */
export const DistrictList = styled('div', {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ compact }) => (compact ? '8px' : '20px')};
  padding-bottom: 36px;
`

/**
 * 시도 헤더 — 광역권역 안에 시도가 2개 이상일 때만 노출 (예: 경상 = 부산광역시 + 경상남도).
 * 디자인 토큰상 "Title 22 - Bold" 비슷한 강조 — 단일 시도일 땐 생략하므로 SiGunGuGroup 헤더(Title 18)와 구분.
 */
export const SiDoTitle = styled.h2`
  margin: 0;
  padding: 0 4px;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: -0.2px;
  color: #1b1c1d;
`

/**
 * subRegion 필터 칩 row.
 * - 좌측 정렬 (디자인 의도)
 * - 지도 위에 위치 — RegionMap 외부에 둠 (광역권역 레벨 상태)
 */
export const FilterRow = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
`

/**
 * Tabs sticky 컨테이너 — 시군구 트리에서 깊이 들어가도 시도 전환을 유지.
 * - viewport top 에 pinned, 본문 위에 띄우기 위해 z-index + 흰 배경
 */
export const StickyTabs = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background: #ffffff;
`

/**
 * IntersectionObserver sentinel — Tabs 바로 위에 위치한 빈 0px 박스.
 * sentinel 이 viewport 에서 벗어나는 시점 = Tabs 가 pinned 되는 시점 = TopButton 노출 조건.
 */
export const Sentinel = styled.div`
  width: 0;
  height: 0;
  pointer-events: none;
`
